import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { Lightning, X, Plus } from '@phosphor-icons/react';

const KINDS = [
  { id: 'mood', label: 'Mood' },
  { id: 'weather', label: 'Weather' },
  { id: 'want', label: 'Want' },
  { id: 'thought', label: 'Thought' },
];
const EMOJI_PRESET = ['🌧️','☀️','🌙','⚡','🔥','🌊','💛','🫧','✨','🌀'];

const fmtAgo = (iso) => {
  const m = (Date.now() - new Date(iso).getTime()) / 60000;
  if (m < 1) return 'just now';
  if (m < 60) return `${Math.floor(m)}m`;
  return `${Math.floor(m / 60)}h`;
};

const PulseComposer = ({ bubble, open, onClose, onSent }) => {
  const [kind, setKind] = useState('mood');
  const [text, setText] = useState('');
  const [emoji, setEmoji] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await api.post(`/bubbles/${bubble.id}/pulse`,
        { kind, text: text.trim(), emoji: emoji || null });
      toast.success('pulsed.');
      onSent?.(); onClose?.();
      setText(''); setEmoji('');
    } catch (err) { toast.error('could not'); }
    finally { setBusy(false); }
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] bg-black/80 backdrop-blur-xl grid place-items-center px-4" onClick={onClose}>
      <div className="w-full max-w-sm glass rounded-3xl p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-3 right-3 p-2 rounded-full hover:bg-white/10 text-white/60"><X size={16} weight="bold" /></button>
        <div className="text-[10px] uppercase tracking-widest text-bx-cyan mb-1">24h pulse</div>
        <div className="font-display font-bold text-xl mb-3">What is {bubble.name} feeling?</div>
        <div className="flex gap-1.5 mb-3">
          {KINDS.map((k) => (
            <button key={k.id} data-testid={`pulse-kind-${k.id}`}
              onClick={() => setKind(k.id)}
              className={`flex-1 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                kind === k.id ? 'border-bx-cyan bg-bx-cyan/10 text-bx-cyan' : 'border-white/10 text-white/65'
              }`}>{k.label}</button>
          ))}
        </div>
        <textarea data-testid="pulse-text" value={text}
          onChange={(e) => setText(e.target.value.slice(0, 120))}
          placeholder="one breath. one line."
          rows={2}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-bx-cyan mb-2" />
        <div className="flex flex-wrap gap-1 mb-3">
          {EMOJI_PRESET.map((e) => (
            <button key={e} onClick={() => setEmoji(emoji === e ? '' : e)}
              data-testid={`pulse-emoji-${e}`}
              className={`text-lg p-1 rounded ${emoji === e ? 'bg-bx-cyan/20' : 'hover:bg-white/5'}`}>{e}</button>
          ))}
        </div>
        <button data-testid="pulse-submit" onClick={submit} disabled={busy || !text.trim()}
          className="w-full btn-volt py-2.5 rounded-full text-sm font-bold disabled:opacity-30">
          {busy ? 'pulsing…' : 'Pulse'}
        </button>
      </div>
    </div>
  );
};

const PulsePanel = ({ bubble, isOwner }) => {
  const [pulses, setPulses] = useState([]);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get(`/bubbles/${bubble.id}/pulses`);
      setPulses(data.pulses || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, [bubble.id]);
  return (
    <div data-testid="pulse-panel" className="glass rounded-2xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightning size={14} weight="duotone" className="text-bx-volt" />
          <span className="text-[10px] uppercase tracking-widest text-bx-volt">pulses · 24h</span>
        </div>
        {isOwner && (
          <button data-testid="open-pulse"
            onClick={() => setOpen(true)}
            className="text-[10px] px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/15 font-bold uppercase tracking-widest flex items-center gap-1">
            <Plus size={10} weight="bold" /> pulse
          </button>
        )}
      </div>
      {pulses.length === 0 ? (
        <div className="text-xs text-white/60">no pulse in the last 24h.</div>
      ) : (
        <div className="space-y-1.5">
          {pulses.map((p) => (
            <motion.div key={p.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="flex items-center gap-2 text-sm">
              {p.emoji && <span className="text-base">{p.emoji}</span>}
              <span className="text-white/80 flex-1">{p.text}</span>
              <span className="text-[10px] text-white/55">{fmtAgo(p.created_at)}</span>
            </motion.div>
          ))}
        </div>
      )}
      <PulseComposer bubble={bubble} open={open} onClose={() => setOpen(false)} onSent={load} />
    </div>
  );
};

export default PulsePanel;
